import {
  AndroidOutlined,
  AppleOutlined,
  CheckCircleOutlined,
  DesktopOutlined,
  DownloadOutlined,
  MoreOutlined,
  PlusSquareOutlined,
} from "@ant-design/icons";
import { getPwaPlatform, PwaPlatform } from "@hooks";
import { Button, Modal, Segmented } from "antd";
import { ReactNode, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import styled from "styled-components";

interface Props {
  open: boolean;
  onClose: () => void;
  // Native install prompt, only available on Chromium (Android / desktop)
  onInstall?: () => void;
}

// iOS share glyph — square with an arrow pointing up; antd has no equivalent
const ShareIcon = () => (
  <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 3v12" />
    <path d="M8 7l4-4 4 4" />
    <path d="M6 11H5a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-8a1 1 0 0 0-1-1h-1" />
  </svg>
);

// Chrome / Edge address-bar install glyph — monitor with a downward arrow
const DesktopInstallIcon = () => (
  <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 4H5a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-3" />
    <path d="M9 20h6" />
    <path d="M12 16v4" />
    <path d="M17 3v8" />
    <path d="M14 8l3 3 3-3" />
  </svg>
);

interface Step {
  key: string;
  icon?: ReactNode;
}

const GUIDE: Record<PwaPlatform, { steps: Step[]; tip: string }> = {
  ios: {
    steps: [
      { key: "PwaGuideIosStep1" },
      { key: "PwaGuideIosStep2", icon: <ShareIcon /> },
      { key: "PwaGuideIosStep3", icon: <PlusSquareOutlined /> },
      { key: "PwaGuideIosStep4" },
      { key: "PwaGuideIosStep5", icon: <CheckCircleOutlined /> },
    ],
    tip: "PwaGuideIosTip",
  },
  android: {
    steps: [
      { key: "PwaGuideAndroidStep1" },
      { key: "PwaGuideAndroidStep2", icon: <MoreOutlined /> },
      { key: "PwaGuideAndroidStep3", icon: <DownloadOutlined /> },
      { key: "PwaGuideAndroidStep4", icon: <CheckCircleOutlined /> },
    ],
    tip: "PwaGuideAndroidTip",
  },
  desktop: {
    steps: [
      { key: "PwaGuideDesktopStep1" },
      { key: "PwaGuideDesktopStep2", icon: <DesktopInstallIcon /> },
      { key: "PwaGuideDesktopStep3", icon: <MoreOutlined /> },
      { key: "PwaGuideDesktopStep4", icon: <CheckCircleOutlined /> },
    ],
    tip: "PwaGuideDesktopTip",
  },
};

const Intro = styled.div`
  font-size: var(--text-sm);
  color: var(--color-text-secondary);
  line-height: var(--leading-normal);
  margin-bottom: var(--space-4);
`;

const StepList = styled.ol`
  list-style: none;
  padding: 0;
  margin: var(--space-5) 0 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
`;

const StepItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: var(--space-3);
`;

const StepNumber = styled.span`
  width: 26px;
  height: 26px;
  border-radius: var(--radius-full);
  background: var(--color-primary);
  color: var(--color-text-on-primary);
  font-size: var(--text-xs);
  font-weight: var(--weight-semibold);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const StepText = styled.div`
  flex: 1;
  min-width: 0;
  font-size: var(--text-sm);
  color: var(--color-text);
  line-height: var(--leading-normal);
  padding-top: 2px;
`;

const StepIcon = styled.span`
  width: 30px;
  height: 30px;
  border-radius: var(--radius-sm);
  background: var(--color-primary-light);
  color: var(--color-primary);
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const Tip = styled.div`
  margin-top: var(--space-5);
  padding: var(--space-3);
  border-radius: var(--radius-sm);
  background: var(--color-bg-subtle);
  border: 1px solid var(--color-border);
  font-size: var(--text-xs);
  color: var(--color-text-secondary);
  line-height: var(--leading-normal);
`;

export const PwaInstallGuide: React.FC<Props> = ({ open, onClose, onInstall }) => {
  const { t } = useTranslation();
  const [detected] = useState(getPwaPlatform);
  const [platform, setPlatform] = useState<PwaPlatform>(detected);

  const guide = GUIDE[platform];
  const canInstallNow = !!onInstall && platform === detected;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      // Reopen on the visitor's own device, even if they browsed other tabs last time
      afterClose={() => setPlatform(detected)}
      title={t("PwaGuideTitle")}
      width={520}
      centered
      footer={[
        <Button key="close" onClick={onClose}>
          {t("PwaGuideClose")}
        </Button>,
        canInstallNow && (
          <Button
            key="install"
            type="primary"
            icon={<DownloadOutlined />}
            onClick={() => {
              onInstall?.();
              onClose();
            }}
          >
            {t("PwaGuideInstallNow")}
          </Button>
        ),
      ]}
    >
      <Intro>{t("PwaGuideIntro")}</Intro>
      <Segmented<PwaPlatform>
        block
        value={platform}
        onChange={setPlatform}
        options={[
          { value: "ios", icon: <AppleOutlined />, label: t("PwaGuidePlatformIos") },
          { value: "android", icon: <AndroidOutlined />, label: t("PwaGuidePlatformAndroid") },
          { value: "desktop", icon: <DesktopOutlined />, label: t("PwaGuidePlatformDesktop") },
        ]}
      />
      <StepList>
        {guide.steps.map((step, index) => (
          <StepItem key={step.key}>
            <StepNumber>{index + 1}</StepNumber>
            <StepText>
              <Trans i18nKey={step.key} components={{ b: <strong /> }} />
            </StepText>
            {step.icon && <StepIcon>{step.icon}</StepIcon>}
          </StepItem>
        ))}
      </StepList>
      <Tip>
        <Trans i18nKey={guide.tip} components={{ b: <strong /> }} />
      </Tip>
    </Modal>
  );
};
